import { isParamChanged } from '@/app/lib/quant/configFormState'
import type { ParamDef, ParamValue } from '@/app/lib/quant/types'
import { Button } from '@/ui/Button'
import { cn } from '@/ui/lib/utils'

function display(def: ParamDef, value: ParamValue | null): string {
  if (value === null) return '(none)'
  if (def.kind === 'bool') return value === true ? 'On' : 'Off'
  return String(value)
}

interface Props {
  defs: ParamDef[]
  /** The recorded params of a run or a live session. */
  params: Record<string, ParamValue>
  noSchema: boolean
  onLoadParams: () => void
  /**
   * Spec 041: supplied (even empty) only for a live session. The last column then widens to
   * 88px and a name listed here is dimmed with `not used live`. Absent keeps a run's markup as it was.
   */
  ignoredNames?: readonly string[]
}

/**
 * The run's parameters, always visible below the Chart/Log panel (spec 038 P10).
 * A fixed 148 px region (spec 038 R-2): a 36 px header and a 112 px scrolling body, so a
 * 74-parameter strategy and a 5-parameter one take the same space.
 */
export function QuantRunParams({ defs, params, noSchema, onLoadParams, ignoredNames }: Props) {
  const rows = defs.map((def) => {
    const value = def.name in params ? params[def.name] : def.default
    const changed = value !== null && isParamChanged(def, value)
    return { def, value, changed }
  })
  const changedCount = rows.filter((r) => r.changed).length
  const live = ignoredNames !== undefined
  const cols = live
    ? 'grid grid-cols-[minmax(160px,1.3fr)_minmax(110px,1fr)_minmax(90px,1fr)_88px] gap-x-4'
    : 'grid grid-cols-[minmax(160px,1.3fr)_minmax(110px,1fr)_minmax(90px,1fr)_64px] gap-x-4'
  const summaryTitle = noSchema
    ? 'Strategy defaults unavailable.'
    : `${changedCount} of ${defs.length} changed from strategy.toml defaults`
  return (
    <div data-panel="params" className="flex h-[148px] shrink-0 flex-col font-mono">
      <div className="flex h-9 shrink-0 items-center gap-4 px-7">
        <span className="shrink-0 text-[10px] tracking-[0.14em] text-paper-50 uppercase">Params</span>
        <span data-params-summary title={summaryTitle} className="min-w-0 flex-1 truncate text-[11px] text-paper-50">
          {noSchema ? (
            'Strategy defaults unavailable'
          ) : (
            <>
              {defs.length} params
              <span className="mx-2 text-paper-35" aria-hidden="true">
                ·
              </span>
              <span className={changedCount > 0 ? 'text-accent-line' : undefined}>{changedCount} changed</span>
            </>
          )}
        </span>
        {!noSchema && (
          <Button variant="secondary" size="sm" className="shrink-0" onClick={onLoadParams}>
            Load params into config
          </Button>
        )}
      </div>
      <div className="h-[112px] shrink-0 overflow-y-auto" tabIndex={0}>
        <div className="max-w-[960px] px-7 pb-3">
          <div className={cn(cols, 'py-2 text-[9px] tracking-[0.16em] text-paper-40 uppercase')}>
            <span>Parameter</span>
            <span>Value</span>
            <span>Default</span>
            <span>Type</span>
          </div>
          {rows.map(({ def, value, changed }) => {
            const ignored = live && ignoredNames.includes(def.name)
            return (
            <div
              key={def.name}
              data-param-row={def.name}
              data-changed={changed}
              data-ignored={ignored ? 'true' : undefined}
              className={cn(cols, 'items-center border-t border-paper-14 py-2 text-[12px]', ignored && 'opacity-50')}
            >
              <span className="truncate text-paper-90" title={def.name}>
                {def.name}
              </span>
              <span className={cn('truncate', changed ? 'font-semibold text-accent-line' : 'text-paper-90')} data-param-value>
                {display(def, value)}
              </span>
              <span className="truncate text-paper-50">{display(def, def.default)}</span>
              <span className={cn('text-[10px] text-paper-40', live && 'truncate')}>{ignored ? 'not used live' : def.kind}</span>
            </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
